import { useLanguage } from "@/contexts/LanguageContext";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  AlertCircle,
  Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BuyTicketPage = () => {
  const { t, language } = useLanguage();

  const formatDate = (date: string) => {
    if (language === "en") {
      return "May 10, 2025";
    }
    return date;
  };

  const formatTime = (time: string) => {
    if (language === "en") {
      const [start, end] = time.split(" - ");
      const formatHour = (hour: string) => {
        const [h, m] = hour.split(":");
        return `${h}:${m} ${Number(h) >= 12 ? "PM" : "AM"}`;
      };
      return `${formatHour(start)} - ${formatHour(end)}`;
    }
    return time;
  };

  return (
    <div className="pt-16">
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto py-16 px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {language === "en" ? "Buy Ticket" : "Kup Bilet"}
          </h1>
          <p className="text-xl opacity-90">
            {language === "en"
              ? "Grand Finale with Sam Childers in Poznań"
              : "Wielki Finał z Samem Childersem w Poznaniu"}
          </p>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4 md:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="mb-8 overflow-hidden rounded-lg shadow-lg">
              <div className="aspect-[16/9] relative">
                <img
                  src="/preview.jpeg"
                  alt={
                    language === "en" ? "Event preview" : "Zapowiedź wydarzenia"
                  }
                  className="w-full h-full object-cover absolute inset-0"
                />
              </div>
            </div>
            <div className="bg-card border p-6 rounded-lg mb-8">
              <h2 className="flex items-center text-2xl font-bold mb-6">
                <AlertCircle className="mr-2 h-6 w-6 text-primary" />
                {language === "en"
                  ? "Important Information"
                  : "Ważne Informacje"}
              </h2>
              <p className="mb-4 text-red-600 font-semibold">
                {language === "en"
                  ? "Limited seats available!"
                  : "Liczba miejsc ograniczona!"}
              </p>
              <div className="flex flex-col space-y-4 mb-6">
                <div className="flex items-center">
                  <Calendar className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{language === "en" ? "Date:" : "Data:"}</strong>{" "}
                    {formatDate("10 maja 2025")}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{language === "en" ? "Time:" : "Godzina:"}</strong>{" "}
                    {formatTime("17:00 - 19:00")}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>
                      {language === "en" ? "Location:" : "Miejsce:"}
                    </strong>{" "}
                    {t("final.subtitle")}, {language === "en" ? "Hall" : "sala"}{" "}
                    B17
                  </span>
                </div>
                <div className="flex items-center">
                  <Users className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>
                      {language === "en" ? "Entry fee:" : "Opłata wstępu:"}
                    </strong>{" "}
                    {language === "en"
                      ? "20 PLN - ticket required"
                      : "20 zł - wymagany bilet"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="relative md:mt-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg shadow-lg p-4 border">
                <h2 className="flex items-center text-2xl font-bold mb-6">
                  <Ticket className="mr-2 h-6 w-6 text-primary" />
                  {language === "en"
                    ? "Buy Event Ticket"
                    : "Kup bilet na wydarzenie"}
                </h2>
                <p className="mb-6 text-muted-foreground">
                  {language === "en"
                    ? "Event registration powered by evenea.pl"
                    : "Rejestracja na wydarzenie obsługiwana przez evenea.pl"}
                </p>
                <div className="relative" style={{ minHeight: "600px" }}>
                  <iframe
                    src="https://app.evenea.pl/event/kaznodziejazkarabinem-poznan/?out=1&source=event_iframe"
                    width="100%"
                    height="100%"
                    style={{
                      border: 0,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Leadership Event Section */}
      <div className="bg-black text-primary-foreground py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg md:text-xl text-primary mb-2">
                {language === "en" 
                  ? "Are you an entrepreneur?" 
                  : "Jesteś przedsiębiorcą?"}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {language === "en" 
                  ? "Sam Childers for Business Leaders" 
                  : "Sam Childers dla liderów biznesu"}
              </h2>
              <p className="text-xl mb-6 opacity-90">
                {language === "en"
                  ? "May 9th (Friday), the day before the Grand Finale. An exclusive event for business leaders and entrepreneurs. Learn from Sam's unique experience in leading teams through extreme challenges and building successful organizations in the most demanding environments."
                  : "9 maja (piątek), dzień przed Wielkim Finałem. Ekskluzywne wydarzenie dla liderów biznesu i przedsiębiorców. Poznaj unikalne doświadczenia Sama w prowadzeniu zespołów przez ekstremalne wyzwania i budowaniu skutecznych organizacji w najbardziej wymagających środowiskach."}
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <Calendar className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{language === "en" ? "Date:" : "Data:"}</strong>{" "}
                    {formatDate("9 maja 2025")}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{language === "en" ? "Time:" : "Godzina:"}</strong>{" "}
                    {formatTime("17:00 - 19:00")}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{language === "en" ? "Location:" : "Miejsce:"}</strong>{" "}
                    Hotel Mercure, Poznań
                  </span>
                </div>
              </div>
              <Button variant="secondary" size="lg" asChild>
                <a href="https://biznes.kaznodziejazkarabinem.pl" target="_blank" rel="noopener noreferrer">
                  {language === "en" ? "Register for Masterclass" : "Zarejestruj się na Masterclass"}
                </a>
              </Button>
            </div>
            <div className="hidden md:block">
              <img
                src="/images/sam-main (1).png"
                alt={language === "en" ? "Business leadership masterclass preview" : "Zapowiedź masterclass przywództwa"}
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyTicketPage;
